import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { api } from '../api'
import { Tenant, TenantUser } from '../types'

interface Props {
  tenants: Tenant[]
  selectedTenantId: string | null
  onSelectTenantId: (id: string | null) => void
  onRefreshData: () => void
}

export function TenantAdminsView({
  tenants,
  selectedTenantId,
  onSelectTenantId,
  onRefreshData,
}: Props) {
  const [adminsMap, setAdminsMap] = useState<Record<string, TenantUser[]>>({})
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Modal States
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<{ user: TenantUser; tenantId: string } | null>(null)

  // Form inputs
  const [formTenantId, setFormTenantId] = useState<string>('')
  const [formFullName, setFormFullName] = useState('')
  const [formEmail, setFormEmail] = useState('')
  const [formPassword, setFormPassword] = useState('')
  const [formIsActive, setFormIsActive] = useState(true)
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Fetch TenantAdmins for either a specific tenant or all tenants
  const fetchTenantAdmins = useCallback(async () => {
    if (tenants.length === 0) return
    setLoading(true)
    setError(null)

    try {
      const targetTenants = selectedTenantId
        ? tenants.filter((t) => t.id === selectedTenantId)
        : tenants

      const results = await Promise.all(
        targetTenants.map(async (t) => {
          try {
            const list = await api.listTenantAdmins(t.id)
            return { tenantId: t.id, list }
          } catch {
            return { tenantId: t.id, list: [] }
          }
        })
      )

      const map: Record<string, TenantUser[]> = {}
      results.forEach((r) => {
        map[r.tenantId] = r.list
      })
      setAdminsMap(map)
    } catch (err: any) {
      setError(err.message || 'Tenant adminleri alınırken hata oluştu.')
    } finally {
      setLoading(false)
    }
  }, [tenants, selectedTenantId])

  useEffect(() => {
    fetchTenantAdmins()
  }, [fetchTenantAdmins])

  // Flattened list of admins with tenant info
  const allAdmins = useMemo(() => {
    const list: { user: TenantUser; tenant: Tenant }[] = []
    tenants.forEach((t) => {
      const tenantUsers = adminsMap[t.id] || []
      tenantUsers.forEach((u) => {
        list.push({ user: u, tenant: t })
      })
    })
    return list
  }, [tenants, adminsMap])

  const filteredAdmins = useMemo(() => {
    return allAdmins.filter(({ user, tenant }) => {
      const matchSearch =
        (user.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase()) ||
        tenant.name.toLowerCase().includes(search.toLowerCase())

      const matchTenant = selectedTenantId ? tenant.id === selectedTenantId : true
      return matchSearch && matchTenant
    })
  }, [allAdmins, search, selectedTenantId])

  const handleOpenCreate = () => {
    setFormTenantId(selectedTenantId || (tenants[0]?.id ?? ''))
    setFormFullName('')
    setFormEmail('')
    setFormPassword('')
    setFormIsActive(true)
    setFormError(null)
    setIsCreateOpen(true)
  }

  const handleOpenEdit = (user: TenantUser, tenantId: string) => {
    setEditingUser({ user, tenantId })
    setFormFullName(user.full_name || '')
    setFormEmail(user.email)
    setFormPassword('')
    setFormIsActive(user.is_active)
    setFormError(null)
  }

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formTenantId) {
      setFormError('Lütfen hedef tenantı seçiniz.')
      return
    }
    if (!formEmail.trim() || !formPassword) {
      setFormError('E-posta ve şifre zorunludur.')
      return
    }
    if (formPassword.length < 6) {
      setFormError('Şifre en az 6 karakter olmalıdır.')
      return
    }

    setFormLoading(true)
    setFormError(null)

    try {
      await api.createTenantAdmin(formTenantId, {
        email: formEmail.trim(),
        password: formPassword,
        full_name: formFullName.trim() || undefined,
      })
      setSuccess(`"${formEmail}" için TenantAdmin hesabı başarıyla oluşturuldu.`)
      setIsCreateOpen(false)
      fetchTenantAdmins()
      onRefreshData()
    } catch (err: any) {
      setFormError(err.message || 'TenantAdmin oluşturulamadı.')
    } finally {
      setFormLoading(false)
    }
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingUser) return

    setFormLoading(true)
    setFormError(null)

    try {
      await api.updateTenantUser(editingUser.tenantId, editingUser.user.id, {
        email: formEmail.trim() || undefined,
        full_name: formFullName.trim() || undefined,
        password: formPassword ? formPassword : undefined,
        is_active: formIsActive,
      })
      setSuccess(`"${formEmail}" hesabı güncellendi.`)
      setEditingUser(null)
      fetchTenantAdmins()
      onRefreshData()
    } catch (err: any) {
      setFormError(err.message || 'Kullanıcı güncellenemedi.')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDeleteAdmin = async (user: TenantUser, tenantId: string) => {
    if (
      !window.confirm(
        `"${user.full_name || user.email}" isimli TenantAdmin hesabını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`
      )
    ) {
      return
    }

    const originalMap = adminsMap
    setAdminsMap((prev) => ({
      ...prev,
      [tenantId]: (prev[tenantId] || []).filter((u) => u.id !== user.id),
    }))

    try {
      await api.deleteTenantUser(tenantId, user.id)
      setSuccess(`TenantAdmin "${user.email}" başarıyla silindi.`)
      fetchTenantAdmins()
      onRefreshData()
    } catch (err: any) {
      setAdminsMap(originalMap)
      alert(err.message || 'Silme işlemi başarısız oldu.')
    }
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="view-title-block">
          <h2>Tenant Yöneticileri (TenantAdmins)</h2>
          <p>
            Her tenant organizasyonuna ait üst düzey yöneticileri atayın, şifrelerini sıfırlayın veya durumlarını değiştirin
          </p>
        </div>
        <div className="view-actions">
          <button type="button" className="btn btn-primary" onClick={handleOpenCreate}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
            <span>Yeni TenantAdmin Ekle</span>
          </button>
        </div>
      </div>

      {success && (
        <div className="alert alert-success">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>{success}</span>
          <button
            type="button"
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => setSuccess(null)}
          >
            ✕
          </button>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Toolbar & Filters */}
      <div className="toolbar-card">
        <div className="search-input-wrapper">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--color-text-dim)' }}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Yönetici adı, e-posta veya tenant ara…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
              onClick={() => setSearch('')}
            >
              ✕
            </button>
          )}
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={selectedTenantId || 'all'}
            onChange={(e) => onSelectTenantId(e.target.value === 'all' ? null : e.target.value)}
          >
            <option value="all">Tüm Tenantlar ({tenants.length})</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={fetchTenantAdmins}
            title="Listeyi Yenile"
            disabled={loading}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            <span>{loading ? 'Yükleniyor…' : 'Yenile'}</span>
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Bağlı Olduğu Tenant</th>
              <th>Yönetici Adı</th>
              <th>E-Posta Adresi</th>
              <th>Yetki Rolü</th>
              <th>Durum</th>
              <th>Kayıt Tarihi</th>
              <th style={{ textAlign: 'right' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filteredAdmins.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="empty-state">
                    <p>
                      {selectedTenantId
                        ? 'Seçili tenanta ait henüz bir TenantAdmin hesabı tanımlanmamış.'
                        : 'Kayıtlı TenantAdmin bulunamadı.'}
                    </p>
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleOpenCreate}>
                      Yeni TenantAdmin Oluştur
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredAdmins.map(({ user, tenant }) => (
                <tr key={user.id}>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 600 }}>{tenant.name}</span>
                      <span style={{ fontSize: '0.74rem', color: 'var(--color-text-dim)' }}>
                        {tenant.slug} ({tenant.tenant_type})
                      </span>
                    </div>
                  </td>
                  <td style={{ fontWeight: 600 }}>{user.full_name || 'İsimsiz Yönetici'}</td>
                  <td>
                    <code>{user.email}</code>
                  </td>
                  <td>
                    <span className="badge badge-admin">TENANT ADMIN</span>
                  </td>
                  <td>
                    <span className={`badge badge-${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--color-text-dim)' }}>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="actions-cell" style={{ justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleOpenEdit(user, tenant.id)}
                        title="Yöneticiyi Düzenle / Şifre Değiştir"
                      >
                        Düzenle
                      </button>

                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteAdmin(user, tenant.id)}
                        title="Yöneticiyi Sil"
                      >
                        Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create TenantAdmin Modal */}
      {isCreateOpen && (
        <div className="modal-backdrop">
          <div className="modal-dialog">
            <div className="modal-header">
              <h3 className="modal-title">Yeni TenantAdmin Oluştur</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsCreateOpen(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateSubmit}>
              <div className="modal-body">
                {formError && <div className="alert alert-danger">{formError}</div>}

                <div className="form-group">
                  <label className="form-label form-label-required">Hedef Tenant Organizasyonu</label>
                  <select
                    className="form-select"
                    value={formTenantId}
                    onChange={(e) => setFormTenantId(e.target.value)}
                    required
                  >
                    <option value="" disabled>
                      Tenant seçiniz…
                    </option>
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.slug})
                      </option>
                    ))}
                  </select>
                  <span className="form-help">
                    TenantAdmin hesabı bu firmanın ERP ortamını yönetme yetkisine sahip olacaktır.
                  </span>
                </div>

                <div className="form-group">
                  <label className="form-label">Yönetici Adı & Soyadı</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formFullName}
                    onChange={(e) => setFormFullName(e.target.value)}
                    placeholder="Örn: Ahmet Yılmaz"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label form-label-required">E-Posta Adresi (Kullanıcı Adı)</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    placeholder="ahmet@firma.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label form-label-required">Geçici / Başlangıç Şifresi</label>
                  <input
                    type="password"
                    className="form-input"
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                    placeholder="En az 6 karakter"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsCreateOpen(false)}
                  disabled={formLoading}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Oluşturuluyor…' : 'TenantAdmin Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit TenantAdmin Modal */}
      {editingUser && (
        <div className="modal-backdrop">
          <div className="modal-dialog">
            <div className="modal-header">
              <h3 className="modal-title">TenantAdmin Düzenle</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setEditingUser(null)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                {formError && <div className="alert alert-danger">{formError}</div>}

                <div className="form-group">
                  <label className="form-label">Yönetici Adı & Soyadı</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formFullName}
                    onChange={(e) => setFormFullName(e.target.value)}
                    placeholder="Ahmet Yılmaz"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label form-label-required">E-Posta Adresi</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Şifre Sıfırlama / Güncelleme (Opsiyonel)</label>
                  <input
                    type="password"
                    className="form-input"
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                    placeholder="Değiştirmek istemiyorsanız boş bırakın"
                    minLength={6}
                  />
                  <span className="form-help">
                    Yalnızca yeni bir şifre tanımlamak istiyorsanız buraya giriniz.
                  </span>
                </div>

                <div className="form-group" style={{ marginTop: '4px' }}>
                  <label className="form-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formIsActive}
                      onChange={(e) => setFormIsActive(e.target.checked)}
                    />
                    <span>Hesap aktif (Giriş yapabilir)</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingUser(null)}
                  disabled={formLoading}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Kaydediliyor…' : 'Değişiklikleri Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
